import React from 'react';
import { Skeleton } from '@mui/material';
import { UserTradeStats } from 'types/interfaces/profile';
import { Points } from 'components/Points/Points';
import { AssetUrls } from 'config/assets';
import './StatsCard.css';

interface StatsCardProps {
  stats: UserTradeStats | undefined;
  isLoading: boolean;
}

// Format number with commas
const formatNumber = (num: number): string => {
  return new Intl.NumberFormat('en-US').format(num);
};

// Format an average duration in days. Null (no qualifying trade) and undefined
// (a backend that predates the field) both read N/A.
export const formatShipTime = (days: number | null | undefined): string => {
  if (days == null) return 'N/A';
  return `${days.toFixed(1)} days`;
};

export const SHIP_TIME_TITLE = 'Average time from a trade opening to the sender marking it shipped.';
export const DELIVERY_TIME_TITLE = 'Average time from shipped to the receiver confirming arrival.';

interface StatItemProps {
  icon?: string;
  iconAlt?: string;
  value: string | number;
  label: string;
  highlight?: boolean;
}

const StatItem: React.FC<StatItemProps> = ({ icon, iconAlt, value, label, highlight }) => (
  <div className={`stats-card-item ${highlight ? 'highlight' : ''}`}>
    <div className="stats-card-item-value">
      <span className={highlight ? 'highlight-value' : ''}>{value}</span>
      {icon && (
        <img
          src={icon}
          alt={iconAlt || ''}
          className="stats-card-item-icon"
          onError={(e) => {
            (e.target as HTMLImageElement).style.display = 'none';
          }}
        />
      )}
    </div>
    <div className="stats-card-item-label">{label}</div>
  </div>
);

export const StatsCard: React.FC<StatsCardProps> = ({ stats, isLoading }) => {
  if (isLoading) {
    return (
      <div className="stats-card stats-card--loading">
        <div className="stats-card-grid">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="stats-card-item">
              <Skeleton variant="text" width={80} height={40} sx={{ backgroundColor: 'rgba(255,255,255,0.1)' }} />
              <Skeleton variant="text" width={60} height={16} sx={{ backgroundColor: 'rgba(255,255,255,0.1)' }} />
            </div>
          ))}
        </div>
        <div className="stats-card-divider" />
        <div className="stats-card-secondary">
          <Skeleton variant="text" width={120} height={20} sx={{ backgroundColor: 'rgba(255,255,255,0.1)' }} />
          <Skeleton variant="text" width={130} height={20} sx={{ backgroundColor: 'rgba(255,255,255,0.1)' }} />
          <Skeleton variant="text" width={100} height={20} sx={{ backgroundColor: 'rgba(255,255,255,0.1)' }} />
        </div>
      </div>
    );
  }

  if (!stats) return null;

  return (
    <div className="stats-card">
      <div className="stats-card-grid">
        <StatItem
          icon={AssetUrls.icons.navigation.sending}
          iconAlt="Sent"
          value={formatNumber(stats.cardsSent)}
          label="Cards Sent"
        />
        <StatItem
          icon={AssetUrls.icons.navigation.receiving}
          iconAlt="Received"
          value={formatNumber(stats.cardsReceived)}
          label="Cards Received"
        />
        <StatItem
          value={formatNumber(stats.totalTrades)}
          label="Total Trades"
        />
        <div className="stats-card-item stats-card-item--trade-value">
          <div className="stats-card-item-value">
            <Points points={stats.totalTradeValue} type="big" />
          </div>
          <div className="stats-card-item-label">Trade Value</div>
        </div>
      </div>

      <div className="stats-card-divider" />

      <div className="stats-card-secondary">
        <div className="stats-card-secondary-item" title={SHIP_TIME_TITLE} data-testid="stats-avg-ship-time">
          <span className="stats-card-secondary-label">Avg Ship Time</span>
          <span className="stats-card-secondary-value">{formatShipTime(stats.avgShipTimeDays)}</span>
        </div>
        <div className="stats-card-secondary-item" title={DELIVERY_TIME_TITLE} data-testid="stats-avg-delivery-time">
          <span className="stats-card-secondary-label">Avg Delivery Time</span>
          <span className="stats-card-secondary-value">{formatShipTime(stats.avgDeliveryTimeDays)}</span>
        </div>
        <div className="stats-card-secondary-item" data-testid="stats-trade-partners">
          <span className="stats-card-secondary-label">Trade Partners</span>
          <span className="stats-card-secondary-value">{formatNumber(stats.uniqueTradePartners)}</span>
        </div>
      </div>
    </div>
  );
};

export default StatsCard;
